(function cultureHouseConsentBridge() {
  'use strict';

  const config = window.CultureHouseConsentConfig || {};
  const previewStorageKey = 'culture_house_preview_consent_v1';
  const isLocalPreview = ['localhost', '127.0.0.1', '0.0.0.0', '::1'].includes(window.location.hostname);
  const integrations = new Map();
  let customerPrivacy = null;
  let preferencesAreOpen = false;
  let preferencesOpener = null;
  let currentState = Object.freeze({
    analytics: false,
    marketing: false,
    preferences: false,
    saleOfData: false,
    ready: false,
    source: 'initializing',
  });
  let resolveReady;
  let hasResolvedReady = false;
  let integrationLoadScheduled = false;
  let pendingIntegrationState = currentState;

  const ready = new Promise((resolve) => {
    resolveReady = resolve;
  });

  if (isLocalPreview) {
    document.documentElement.setAttribute('data-culture-house-local-preview', '');
  }

  window.CultureHouseConsent = {
    ready,
    getState: () => ({ ...currentState }),
    openPreferences,
    register(name, callback) {
      if (typeof callback !== 'function') return () => {};

      integrations.set(name, callback);
      if (currentState.ready) callback({ ...currentState });

      return () => integrations.delete(name);
    },
  };

  function consentValue(value) {
    return value ? 'granted' : 'denied';
  }

  function normalizeState(state, source) {
    return Object.freeze({
      analytics: state.analytics === true,
      marketing: state.marketing === true,
      preferences: state.preferences === true,
      saleOfData: state.saleOfData === true,
      ready: true,
      source,
    });
  }

  function stateFromShopify(detail) {
    return {
      analytics: detail?.analyticsAllowed ?? customerPrivacy?.analyticsProcessingAllowed?.() ?? false,
      marketing: detail?.marketingAllowed ?? customerPrivacy?.marketingAllowed?.() ?? false,
      preferences: detail?.preferencesAllowed ?? customerPrivacy?.preferencesProcessingAllowed?.() ?? false,
      saleOfData: detail?.saleOfDataAllowed ?? customerPrivacy?.saleOfDataAllowed?.() ?? false,
    };
  }

  function updateGoogleConsent(state) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(['consent', 'update', {
      analytics_storage: consentValue(state.analytics),
      ad_storage: consentValue(state.marketing),
      ad_user_data: consentValue(state.marketing),
      ad_personalization: consentValue(state.marketing),
      functionality_storage: consentValue(state.preferences),
      personalization_storage: consentValue(state.preferences),
      security_storage: 'granted',
    }]);
  }

  function scheduleConsentedIntegrations(state) {
    pendingIntegrationState = state;
    if ((!state.analytics && !state.marketing) || integrationLoadScheduled) return;
    integrationLoadScheduled = true;

    const loadWhenIdle = () => {
      const load = () => {
        integrationLoadScheduled = false;
        const latestState = pendingIntegrationState;
        if (!latestState.analytics && !latestState.marketing) return;
        ensureGoogleTagManager(latestState);
        ensureMicrosoftClarity(latestState);
        updateMicrosoftClarity(latestState);
      };

      if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 3000 });
      else window.setTimeout(load, 1000);
    };

    if (document.readyState === 'complete') loadWhenIdle();
    else window.addEventListener('load', loadWhenIdle, { once: true });
  }

  function ensureGoogleTagManager(state) {
    const id = String(config.googleTagManagerId || '').trim();
    if (!/^GTM-[A-Z0-9]+$/i.test(id) || (!state.analytics && !state.marketing)) return;
    if (document.querySelector('script[data-culture-house-gtm]')) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.dataset.cultureHouseGtm = id;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
  }

  function ensureMicrosoftClarity(state) {
    const id = String(config.microsoftClarityId || '').trim();
    if (!/^[a-z0-9]+$/i.test(id) || !state.analytics) return;

    if (!window.clarity) {
      window.clarity = function clarityQueue() {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };
    }

    if (!document.querySelector('script[data-culture-house-clarity]')) {
      const script = document.createElement('script');
      script.async = true;
      script.dataset.cultureHouseClarity = id;
      script.src = `https://www.clarity.ms/tag/${encodeURIComponent(id)}`;
      document.head.appendChild(script);
    }
  }

  function updateMicrosoftClarity(state) {
    if (typeof window.clarity !== 'function') return;

    window.clarity('consentv2', {
      ad_Storage: consentValue(state.marketing),
      analytics_Storage: consentValue(state.analytics),
    });
  }

  function publishState(state, source) {
    const nextState = normalizeState(state, source);
    const isSameState =
      currentState.ready &&
      currentState.analytics === nextState.analytics &&
      currentState.marketing === nextState.marketing &&
      currentState.preferences === nextState.preferences &&
      currentState.saleOfData === nextState.saleOfData &&
      currentState.source === nextState.source;

    currentState = nextState;
    updateGoogleConsent(nextState);
    updateMicrosoftClarity(nextState);
    scheduleConsentedIntegrations(nextState);

    if (!isSameState) {
      window.dataLayer?.push({
        event: 'culture_house_consent_update',
        culture_house_consent: {
          analytics: nextState.analytics,
          marketing: nextState.marketing,
          preferences: nextState.preferences,
          sale_of_data: nextState.saleOfData,
          source: nextState.source,
        },
      });

      document.dispatchEvent(
        new CustomEvent('cultureHouseConsentChanged', {
          detail: { ...nextState },
        })
      );

      integrations.forEach((callback) => callback({ ...nextState }));
    }

    if (!hasResolvedReady) {
      hasResolvedReady = true;
      resolveReady({ ...nextState });
    }
  }

  function getPreviewState() {
    if (!isLocalPreview) return null;

    try {
      const stored = JSON.parse(window.localStorage.getItem(previewStorageKey));
      if (!stored || stored.version !== 1) return null;
      return stored.consent;
    } catch (_error) {
      return null;
    }
  }

  function setPreviewState(consent) {
    window.localStorage.setItem(
      previewStorageKey,
      JSON.stringify({ version: 1, consent, savedAt: new Date().toISOString() })
    );
    document.documentElement.setAttribute('data-culture-house-local-consent', '');
  }

  function hideNativeConsentUi() {
    const selectors = preferencesAreOpen ? ['#shopify-pc__banner'] : ['#shopify-pc__banner', '#shopify-pc__prefs'];
    selectors.forEach((selector) => {
      const element = document.querySelector(selector);
      if (!element) return;
      element.style.setProperty('display', 'none', 'important');
      element.setAttribute('aria-hidden', 'true');
    });
  }

  function closePreferences() {
    preferencesAreOpen = false;
    const preferences = document.querySelector('#shopify-pc__prefs');
    preferences?.style.setProperty('display', 'none', 'important');
    preferences?.setAttribute('aria-hidden', 'true');

    const customDialog = document.querySelector('[data-culture-house-consent-dialog]');
    if (customDialog instanceof HTMLDialogElement && customDialog.open) customDialog.close();

    const opener = preferencesOpener;
    preferencesOpener = null;
    window.requestAnimationFrame(() => opener?.focus());
  }

  function showConsentError(message) {
    const customError = document.querySelector('[data-culture-house-consent-error]');
    if (customError) {
      customError.textContent = message;
      customError.hidden = false;
      return;
    }

    const banner = document.querySelector('#shopify-pc__banner');
    if (!banner) return;

    let error = banner.querySelector('[data-culture-house-consent-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.cultureHouseConsentError = '';
      error.setAttribute('role', 'alert');
      banner.querySelector('.shopify-pc__banner__body')?.appendChild(error);
    }
    error.textContent = message;
  }

  function setButtonsBusy(isBusy) {
    document
      .querySelectorAll(
        '#shopify-pc__banner__btn-accept, #shopify-pc__banner__btn-decline, #shopify-pc__prefs__header-accept, #shopify-pc__prefs__header-decline, #shopify-pc__prefs__header-save'
          + ', [data-culture-house-consent-action]'
      )
      .forEach((button) => {
        button.disabled = isBusy;
        button.setAttribute('aria-busy', String(isBusy));
      });
  }

  function saveConsent(consent) {
    setButtonsBusy(true);

    if (isLocalPreview) {
      setPreviewState(consent);
      publishState(consent, 'local-preview');
      hideNativeConsentUi();
      closePreferences();
      setButtonsBusy(false);
      return;
    }

    if (!customerPrivacy) {
      showConsentError('Cookie preferences are still loading. Please try again.');
      setButtonsBusy(false);
      return;
    }

    customerPrivacy.setTrackingConsent(
      {
        analytics: consent.analytics,
        marketing: consent.marketing,
        preferences: consent.preferences,
      },
      (response) => {
        if (response?.error) {
          showConsentError('We could not save your cookie preferences. Please try again.');
          setButtonsBusy(false);
          return;
        }

        try {
          window.localStorage.removeItem(previewStorageKey);
        } catch (_error) {
          // Consent still persists through Shopify when storage is unavailable.
        }
        publishState(stateFromShopify(), 'shopify');
        hideNativeConsentUi();
        closePreferences();
        setButtonsBusy(false);
      }
    );
  }

  function readGranularPreferences() {
    const customDialog = document.querySelector('[data-culture-house-consent-dialog]');
    if (customDialog instanceof HTMLDialogElement && customDialog.open) {
      return {
        analytics: customDialog.querySelector('[data-culture-house-consent="analytics"]')?.checked === true,
        marketing: customDialog.querySelector('[data-culture-house-consent="marketing"]')?.checked === true,
        preferences: customDialog.querySelector('[data-culture-house-consent="preferences"]')?.checked === true,
        saleOfData: currentState.saleOfData,
      };
    }

    return {
      analytics: document.querySelector('#shopify-pc__prefs__analytics-input')?.checked === true,
      marketing: document.querySelector('#shopify-pc__prefs__marketing-input')?.checked === true,
      preferences: document.querySelector('#shopify-pc__prefs__preferences-input')?.checked === true,
      saleOfData: currentState.saleOfData,
    };
  }

  function openPreferences() {
    preferencesOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const customDialog = document.querySelector('[data-culture-house-consent-dialog]');
    if (customDialog instanceof HTMLDialogElement) {
      preferencesAreOpen = true;
      customDialog.querySelectorAll('[data-culture-house-consent]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.checked = currentState[input.dataset.cultureHouseConsent] === true;
      });
      const error = customDialog.querySelector('[data-culture-house-consent-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      if (!customDialog.open) customDialog.showModal();
      window.requestAnimationFrame(() => customDialog.querySelector('[data-culture-house-consent-close]')?.focus());
      return true;
    }

    const preferences = document.querySelector('#shopify-pc__prefs');
    if (preferences) {
      const banner = document.querySelector('#shopify-pc__banner');
      preferencesAreOpen = true;
      banner?.style.setProperty('display', 'none', 'important');
      banner?.setAttribute('aria-hidden', 'true');
      preferences.style.setProperty('display', 'block', 'important');
      preferences.removeAttribute('aria-hidden');
      window.requestAnimationFrame(() => {
        preferences.querySelector('#shopify-pc__prefs__header-close')?.focus();
      });
      return true;
    }

    const manageButton = document.querySelector('#shopify-pc__banner__btn-manage-prefs');
    manageButton?.click();
    return Boolean(manageButton);
  }

  function interceptConsentControls(event) {
    if (!(event.target instanceof Element)) return;

    const customPreferencesButton = event.target.closest('[data-culture-house-cookie-preferences]');
    if (customPreferencesButton) {
      event.preventDefault();
      openPreferences();
      return;
    }

    const consentAction = event.target.closest('[data-culture-house-consent-action]');
    if (consentAction) {
      event.preventDefault();
      const action = consentAction.dataset.cultureHouseConsentAction;
      if (action === 'accept') {
        saveConsent({ analytics: true, marketing: true, preferences: true, saleOfData: currentState.saleOfData });
      } else if (action === 'decline') {
        saveConsent({ analytics: false, marketing: false, preferences: false, saleOfData: currentState.saleOfData });
      } else if (action === 'save') {
        saveConsent(readGranularPreferences());
      }
      return;
    }

    if (event.target.closest('[data-culture-house-consent-close]')) {
      event.preventDefault();
      closePreferences();
      return;
    }

    if (event.target.closest('#shopify-pc__prefs__overlay, #shopify-pc__prefs__header-close')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closePreferences();
      return;
    }

    const button = event.target.closest('button');
    if (!button) return;

    const acceptButtons = ['shopify-pc__banner__btn-accept', 'shopify-pc__prefs__header-accept'];
    const declineButtons = ['shopify-pc__banner__btn-decline', 'shopify-pc__prefs__header-decline'];
    const isSaveButton = button.id === 'shopify-pc__prefs__header-save';
    if (!acceptButtons.includes(button.id) && !declineButtons.includes(button.id) && !isSaveButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (acceptButtons.includes(button.id)) {
      saveConsent({ analytics: true, marketing: true, preferences: true, saleOfData: currentState.saleOfData });
    } else if (declineButtons.includes(button.id)) {
      saveConsent({ analytics: false, marketing: false, preferences: false, saleOfData: currentState.saleOfData });
    } else {
      saveConsent(readGranularPreferences());
    }
  }

  function initializeLocalPreview() {
    const previewState = getPreviewState();
    hideNativeConsentUi();

    if (previewState) {
      document.documentElement.setAttribute('data-culture-house-local-consent', '');
      publishState(previewState, 'local-preview');
      return;
    }

    publishState({}, 'local-preview');
    openPreferences();
  }

  function initializeShopifyPrivacy() {
    if (isLocalPreview) {
      initializeLocalPreview();
      return;
    }

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      window.setTimeout(initializeShopifyPrivacy, 50);
      return;
    }

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error || !window.Shopify.customerPrivacy) {
        publishState({}, 'unavailable');
        return;
      }

      customerPrivacy = window.Shopify.customerPrivacy;
      publishState(stateFromShopify(), 'shopify');
    });
  }

  document.addEventListener('click', interceptConsentControls, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && preferencesAreOpen) {
      event.preventDefault();
      closePreferences();
    }
  });
  document.addEventListener('visitorConsentCollected', (event) => {
    const consent = stateFromShopify(event.detail);

    if (isLocalPreview) {
      setPreviewState(consent);
      publishState(consent, 'local-preview');
      preferencesAreOpen = false;
      hideNativeConsentUi();
      return;
    }

    if (!customerPrivacy) return;
    publishState(consent, 'shopify');
    preferencesAreOpen = false;
    hideNativeConsentUi();
  });

  document.querySelector('[data-culture-house-consent-dialog]')?.addEventListener('cancel', (event) => {
    event.preventDefault();
    closePreferences();
  });

  initializeShopifyPrivacy();
})();
